import { useState, useEffect } from 'react';
import { getProductImage } from '../../utils/imageStorage';
import LocalImageUploader from '../../components/feature/LocalImageUploader';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import InventoryManager from '../../components/feature/InventoryManager';
import StockDisplay from '../../components/feature/StockDisplay';
import { useCart } from '../../hooks/useCart';
import { type InventoryData } from '../../components/feature/StockDisplay';

export default function ProduitSapinNoelLivrePlie() {
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState('pompon-rouge');
  const [customImage, setCustomImage] = useState('');
  const [inventory, setInventory] = useState<InventoryData | null>(null);
  const productId = 'sapin-noel-livre-plie';
  const defaultImage = 'https://public.readdy.ai/ai/img_res/fac9d91570db1ae006af1f9129d674e6.jpg';
  const [productImage, setProductImage] = useState<string>(defaultImage);
  const [showImageUploader, setShowImageUploader] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      const savedImage = await getProductImage(productId);
      if (savedImage) {
        setProductImage(savedImage);
      }
    };
    loadImage();
  }, []);

  const handleImageUpdate = (newImageUrl: string) => {
    setProductImage(newImageUrl || defaultImage);
    setShowImageUploader(false);
    window.location.reload();
  };

  const product = {
    id: 'sapin-noel-livre-plie',
    name: 'Sapin de Noël en livre plié',
    price: 30,
    category: 'Collection de Noël',
    subcategory: 'Sapins de Noël en livres',
    description: "Sapin réalisé à partir de pages pliées d'un véritable livre. Chaque modèle est unique et décoré à la main. Deux versions disponibles : pompon rouge ou pompon blanc.",
    image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/1efa15a3cda270c684d125f4f4d3ec67.png',
    link: '/produit-sapin-noel-livre-plie'
  };

  const variants = [
    { 
      id: 'pompon-rouge', 
      name: 'Pompon rouge',
      image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/1efa15a3cda270c684d125f4f4d3ec67.png'
    },
    { 
      id: 'pompon-blanc', 
      name: 'Pompon blanc',
      image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/b25886b6d5302fa4fce5dc966e858d39.png'
    }
  ];

  const handleAddToCart = async () => {
    const productWithVariant = {
      ...product,
      name: `${product.name} - ${variants.find(v => v.id === selectedVariant)?.name || variants[0].name}`,
      variant: selectedVariant
    };
    
    try {
      addItem(productWithVariant, quantity);
      
      // Diminuer le stock si nécessaire
      await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/inventory-management?action=decrease`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            product_id: product.id,
            variant_key: selectedVariant,
            quantity
          }),
        }
      );
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
    }
  };

  const isOutOfStock = () => {
    if (!inventory) return false;
    
    if (inventory.is_made_to_order || inventory.unlimited_stock) {
      return false;
    }
    
    if (inventory.variants.length > 0) {
      const variant = inventory.variants.find(v => v.key === selectedVariant);
      return variant ? variant.stock === 0 && !variant.unlimited : false;
    }
    
    return inventory.stock_quantity === 0;
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      <Header />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <nav className="flex flex-wrap items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-[#6B5E5A] mb-6 sm:mb-8">
            <a href="/" className="hover:text-[#C49B86] transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Accueil
            </a>
            <i className="ri-arrow-right-s-line text-[#C49B86]"></i>
            <a href="/boutique" className="hover:text-[#C49B86] transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Boutique
            </a>
            <i className="ri-arrow-right-s-line text-[#C49B86]"></i>
            <span className="text-[#C49B86] font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>Sapin de Noël en livre plié</span>
          </nav>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 mb-12 sm:mb-16">
            {/* Image du produit */}
            <div className="space-y-4">
              <div className="aspect-square bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-[#C49B86]/20">
                <img
                  src={variants.find(v => v.id === selectedVariant)?.image || variants[0].image}
                  alt={`${product.name} - ${variants.find(v => v.id === selectedVariant)?.name || variants[0].name}`}
                  className="w-full h-full object-cover object-top cursor-pointer hover:scale-105 transition-transform duration-300"
                  onClick={() => window.open(variants.find(v => v.id === selectedVariant)?.image || variants[0].image, '_blank')}
                />
              </div>
            </div>

            {/* Informations du produit */}
            <div className="space-y-4 sm:space-y-6">
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#6B5E5A] mb-3 sm:mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  {product.name}
                </h1>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6">
                  <span className="text-3xl sm:text-4xl font-bold text-[#C49B86]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {product.price.toFixed(2)} €
                  </span>
                  <span className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>TTC</span>
                </div>
                
                {/* Affichage du stock */}
                <StockDisplay 
                  productId={product.id} 
                  selectedVariant={selectedVariant}
                  className="mb-4"
                />
              </div>

              <div className="prose prose-gray max-w-none">
                <p className="text-[#6B5E5A] leading-relaxed text-base sm:text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {product.description}
                </p>
              </div>

              {/* Sélection de variante */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border-2 border-[#C49B86]/20">
                <h3 className="font-semibold text-[#C49B86] mb-3 sm:mb-4 text-lg sm:text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Couleur du pompon
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {variants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => {
                        setSelectedVariant(variant.id);
                      }}
                      className={`p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                        selectedVariant === variant.id
                          ? 'border-[#C49B86] bg-[#C49B86]/10 text-[#C49B86]'
                          : 'border-[#C49B86]/30 hover:border-[#C49B86]/50 text-[#6B5E5A]'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          variant.id === 'pompon-rouge' ? 'bg-red-500 border-red-600' : 'bg-white border-gray-300'
                        }`}></div>
                        <span className="font-medium text-sm sm:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          {variant.name}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Gestion des stocks */}
              <InventoryManager
                productId={product.id}
                productName={product.name}
                category="Collection de Noël"
                subcategory="Sapins de Noël"
                onInventoryUpdate={setInventory}
              />

              {/* Caractéristiques */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border-2 border-[#C49B86]/20">
                <h3 className="font-semibold text-[#C49B86] mb-3 sm:mb-4 text-lg sm:text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Caractéristiques
                </h3>
                <ul className="space-y-2 sm:space-y-3 text-[#6B5E5A]">
                  <li className="flex items-center text-sm sm:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <i className="ri-check-line text-[#C49B86] mr-2 sm:mr-3 text-base sm:text-lg"></i>
                    Fait main avec amour
                  </li>
                  <li className="flex items-center text-sm sm:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <i className="ri-check-line text-[#C49B86] mr-2 sm:mr-3 text-base sm:text-lg"></i>
                    Matériau : Pages de livre recyclées
                  </li>
                  <li className="flex items-center text-sm sm:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <i className="ri-check-line text-[#C49B86] mr-2 sm:mr-3 text-base sm:text-lg"></i>
                    Hauteur : environ 15-20 cm
                  </li>
                  <li className="flex items-center text-sm sm:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <i className="ri-check-line text-[#C49B86] mr-2 sm:mr-3 text-base sm:text-lg"></i>
                    Pompon décoratif au sommet
                  </li>
                  <li className="flex items-center text-sm sm:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <i className="ri-check-line text-[#C49B86] mr-2 sm:mr-3 text-base sm:text-lg"></i>
                    Décoration écologique et unique
                  </li>
                  <li className="flex items-center text-sm sm:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <i className="ri-check-line text-[#C49B86] mr-2 sm:mr-3 text-base sm:text-lg"></i>
                    Parfait pour les fêtes de Noël
                  </li>
                </ul>
              </div>

              {/* Sélection quantité et ajout panier */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border-2 border-[#C49B86]/20">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6">
                  <label className="font-medium text-[#6B5E5A] text-base sm:text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Quantité :</label>
                  <div className="flex items-center border-2 border-[#C49B86]/30 rounded-lg bg-[#FAF9F8] w-fit">
                    <button
                      onClick={() =>
                        setQuantity(Math.max(1, quantity - 1))
                      }
                      className="p-2 sm:p-3 hover:bg-[#C49B86]/10 transition-colors text-[#C49B86]"
                    >
                      <i className="ri-subtract-line text-base sm:text-lg"></i>
                    </button>
                    <span className="px-4 sm:px-6 py-2 sm:py-3 font-medium text-[#6B5E5A] text-base sm:text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-2 sm:p-3 hover:bg-[#C49B86]/10 transition-colors text-[#C49B86]"
                    >
                      <i className="ri-add-line text-base sm:text-lg"></i>
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={isOutOfStock()}
                  className="w-full bg-[#C49B86] text-white py-3 sm:py-4 px-4 sm:px-6 rounded-full hover:bg-[#B97A72] transition-colors font-medium text-base sm:text-lg whitespace-nowrap shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {isOutOfStock() ? (
                    <>
                      <i className="ri-close-circle-line mr-2"></i>
                      Rupture de stock
                    </>
                  ) : (
                    <>
                      <i className="ri-shopping-cart-line mr-2"></i>
                      Ajouter au panier -{' '}
                      {(product.price * quantity).toFixed(2)} €
                    </>
                  )}
                </button>
                
                <div className="mt-3 text-center text-xs sm:text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Variante sélectionnée : <span className="font-medium text-[#C49B86]">{variants.find(v => v.id === selectedVariant)?.name || variants[0].name}</span>
                </div>
              </div>

              {/* Informations livraison */}
              <div className="bg-[#EBD2CF] p-4 sm:p-6 rounded-2xl border-2 border-[#C49B86]/20">
                <h3 className="font-semibold text-[#C49B86] mb-2 sm:mb-3 text-base sm:text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <i className="ri-truck-line mr-2"></i>
                  Livraison
                </h3>
                <ul className="space-y-1 sm:space-y-2 text-[#6B5E5A] text-sm sm:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <li>• Livraison gratuite dès 15 € d'achat</li>
                  <li>• Expédition sous 2-3 jours ouvrés</li>
                  <li>• Emballage soigné et sécurisé</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
